import React, { useEffect, useState } from 'react';
import { 
  ArrowRight, Download, Mail, Linkedin, Phone, MapPin,
  CheckCircle2, FileText, Users, Target, TrendingUp, Award,
  Calendar, Briefcase, GitBranch, MessageSquare, Settings,
  BarChart3, Clipboard, Database, Globe, Shield, Zap,
  ChevronRight, ExternalLink, Clock, DollarSign
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';

const PremiumHome = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [isScrolled, setIsScrolled] = useState(false);
  const [countersStarted, setCountersStarted] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      
      if (window.scrollY > 300 && !countersStarted) {
        setCountersStarted(true);
      }
      
      const sections = ['hero', 'about', 'expertise', 'projects', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [countersStarted]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const achievements = [
    { icon: Award, value: '24+', label: 'Projects Delivered', color: '#3B82F6' },
    { icon: Target, value: '91%', label: 'Success Rate', color: '#3B82F6' },
    { icon: TrendingUp, value: '80%+', label: 'Sprint Achievement', color: '#3B82F6' },
    { icon: Calendar, value: '5+', label: 'Years Experience', color: '#3B82F6' }
  ];

  const expertise = [
    {
      icon: GitBranch,
      title: 'Agile Scrum & Kanban',
      description: 'Sprint planning, backlog management, velocity tracking, daily standups, retrospectives, and adaptive delivery frameworks'
    },
    {
      icon: Shield,
      title: 'CMMI L3 Governance',
      description: 'PMO standards implementation, process audits, quality gates, and organizational process improvement initiatives'
    },
    {
      icon: BarChart3,
      title: 'Strategic Project Leadership',
      description: 'End-to-end project lifecycle management from sales to delivery, including budgeting, costing, and proposal preparation'
    },
    {
      icon: Users,
      title: 'Stakeholder Alignment',
      description: 'Managing cross-functional teams, client onboarding, escalation management, and expectation coordination'
    },
    {
      icon: Target,
      title: 'Sprint Velocity Optimization',
      description: 'Maintaining 80%+ commitment achievement through burndown tracking, resource optimization, and predictive analytics'
    },
    {
      icon: Settings,
      title: 'Release & Deployment Management',
      description: 'Release planning, go-live coordination, rollback strategies, hypercare support, and post-launch KPI tracking'
    },
    {
      icon: Clipboard,
      title: 'QA & UAT Coordination',
      description: 'Test planning, defect triage, severity prioritization, quality gates, and release readiness validation'
    },
    {
      icon: Zap,
      title: 'AI-Powered Project Automation',
      description: 'Leveraging ChatGPT, Claude, and Copilot for reporting automation, health prediction, and documentation generation'
    }
  ];

  const tools = [
    { category: 'Project Management', items: ['Jira', 'Trello', 'ClickUp', 'Asana', 'Microsoft Project'] },
    { category: 'Documentation', items: ['Confluence', 'Notion', 'Google Workspace', 'MS Office Suite'] },
    { category: 'Design & Mapping', items: ['Figma', 'Draw.io', 'Lucidchart', 'Miro', 'Canva'] },
    { category: 'Technical Knowledge', items: ['SQL Basics', 'API Understanding', 'SDLC', 'Agile/Scrum', 'CMMI L3'] },
    { category: 'Development Tools', items: ['Jenkins', 'AWS Basics', 'Git', 'Postman'] },
    { category: 'AI & Automation', items: ['ChatGPT', 'Claude', 'Microsoft Copilot', 'Automation Scripts'] }
  ];

  const projects = [
    {
      title: 'MCDC ERP System',
      industry: 'Government • Agriculture',
      role: 'Senior Project Manager - Agile Delivery Leader',
      problem: 'Maharashtra State Fertilizer Distribution needed a centralized system to connect FPCs, suppliers, and MCDC admin for streamlined fertilizer procurement.',
      approach: 'Led requirement gathering workshops with government stakeholders, created comprehensive BRD/SRS documents, coordinated UAT, and managed cross-functional teams.',
      deliverables: ['Business Requirements Document', 'System Requirement Specification', 'Process Flow Diagrams', 'User Stories & Acceptance Criteria', 'UAT Test Plans'],
      tools: ['Jira', 'Confluence', 'Draw.io', 'Excel'],
      impact: 'Digitized supply chain for 500+ FPCs, reduced processing time by 60%',
      outcome: 'Successfully delivered on time with 95% stakeholder satisfaction'
    },
    {
      title: 'Big-Up.ai Platform',
      industry: 'SaaS • EdTech',
      role: 'Project Manager & PMO Lead',
      problem: 'Need for an AI-powered psychometric assessment platform serving African markets with localized requirements.',
      approach: 'Conducted market research, gathered requirements from international clients, managed agile sprints, and coordinated product roadmap.',
      deliverables: ['Product Roadmap', 'Feature Specifications', 'User Journey Maps', 'Sprint Planning Documents', 'Client Feedback Reports'],
      tools: ['ClickUp', 'Figma', 'Google Analytics', 'Miro'],
      impact: '5,000+ active users, successful market entry in 3 African countries',
      outcome: 'Platform scaled to handle 10x user growth within 6 months'
    },
    {
      title: 'NetCampus School ERP',
      industry: 'Education • Enterprise',
      role: 'Senior Project Manager',
      problem: 'Educational institutions needed a comprehensive AI-enabled management system for academic operations across 1,100+ schools.',
      approach: 'Led requirement elicitation for multi-tenant architecture, managed stakeholder expectations, coordinated release planning, and ensured CMMI L3 compliance.',
      deliverables: ['Requirements Traceability Matrix', 'Change Request Management', 'Release Notes', 'Training Materials', 'System Documentation'],
      tools: ['Jira', 'Confluence', 'Lucidchart', 'MS Project'],
      impact: '1M+ students managed, 1,100+ institutes onboarded successfully',
      outcome: 'Achieved 92% feature adoption rate and 88% client retention'
    }
  ];

  const journey = [
    {
      year: '2019',
      title: 'Started Professional Journey',
      company: 'SAARC Cases',
      description: 'Began as Project Executive, learning project coordination and stakeholder management'
    },
    {
      year: '2020',
      title: 'Transitioned to Business Analysis',
      company: 'SAARC Cases',
      description: 'Started requirement gathering, documentation, and process mapping for software projects'
    },
    {
      year: '2021',
      title: 'Senior Project Executive',
      company: 'SAARC Cases',
      description: 'Led multiple projects, handled international clients, and implemented structured delivery processes'
    },
    {
      year: '2023',
      title: 'Senior Project Manager',
      company: 'Encureit Systems',
      description: 'Managing enterprise projects with CMMI L3 compliance, leading cross-functional teams'
    },
    {
      year: 'Present',
      title: 'Digital Transformation Leadership',
      company: 'Encureit Systems',
      description: 'Driving AI-powered project workflows, PMO governance, and organizational transformation'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-lg shadow-sm border-b border-gray-100' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center space-x-3">
              <img 
                src="/images/logo.png"
                alt="Adarsh Sharma"
                className="h-10 w-10 object-contain"
              />
              <div>
                <div className="flex items-center space-x-2">
                  <span className="text-lg font-bold text-gray-900">ADARSH</span>
                  <span className="text-lg font-bold text-blue-600">SHARMA</span>
                </div>
                <p className="text-xs text-gray-600">Agile Delivery & PMO Leader</p>
              </div>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              {['About', 'Expertise', 'Projects', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors relative group"
                >
                  {item}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 transition-all group-hover:w-full" />
                </button>
              ))}
              <Button
                onClick={() => window.open('https://customer-assets.emergentagent.com/job_pmo-excellence-hub/artifacts/zgt4rgwl_Adarsh_Sharma_Resume_Final.pdf', '_blank')}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Download className="mr-2 h-4 w-4" />
                Resume
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="pt-32 pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <Badge className="bg-blue-50 text-blue-700 border-blue-200 px-4 py-2 text-sm font-semibold">
                  Agile Delivery Leader | PMO Excellence | Strategic Project Leadership
                </Badge>
                <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Leading Agile Delivery &
                  <span className="text-blue-600"> PMO Excellence</span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Driving digital transformation through CMMI L3-aligned governance, strategic project leadership, and high-performance agile delivery across 24+ successful enterprise projects.
                </p>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Button
                  size="lg"
                  onClick={() => scrollToSection('projects')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-base font-semibold"
                >
                  View Projects
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => scrollToSection('contact')}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50 px-8 py-6 text-base font-semibold"
                >
                  Get in Touch
                </Button>
              </div>

              <div className="flex items-center gap-8 pt-4">
                <div>
                  <p className="text-3xl font-bold text-gray-900">24+</p>
                  <p className="text-sm text-gray-600">Projects Delivered</p>
                </div>
                <div className="h-12 w-px bg-gray-200" />
                <div>
                  <p className="text-3xl font-bold text-gray-900">91%</p>
                  <p className="text-sm text-gray-600">Success Rate</p>
                </div>
                <div className="h-12 w-px bg-gray-200" />
                <div>
                  <p className="text-3xl font-bold text-gray-900">5+</p>
                  <p className="text-sm text-gray-600">Years Experience</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl blur-3xl opacity-50" />
              <img
                src="/images/portrait.png"
                alt="Adarsh Sharma"
                className="relative rounded-3xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Professional Summary */}
      <section id="about" className="py-24 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-white border-gray-200 text-gray-700 px-4 py-2 mb-4">
              Professional Profile
            </Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Agile Delivery & PMO Excellence
            </h2>
          </div>

          <Card className="bg-white border-none shadow-lg">
            <CardContent className="p-12">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                I&apos;m a <span className="font-semibold text-gray-900">Senior Project Manager and Agile Delivery Leader</span> with 4+ years of experience in CMMI L3-aligned project governance, strategic planning, and high-velocity agile execution.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                I specialize in <span className="font-semibold text-gray-900">PMO governance, sprint planning, stakeholder alignment, and cross-functional delivery orchestration</span> across enterprise and government projects. My approach combines rigorous CMMI L3 standards with adaptive agile methodologies (Scrum & Kanban).
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                I&apos;ve successfully delivered <span className="font-semibold text-gray-900">24+ projects</span> with a <span className="font-semibold text-gray-900">91% success rate</span> and <span className="font-semibold text-gray-900">80%+ sprint commitment achievement</span>, managing international clients and leading teams through complex digital transformation initiatives.
              </p>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="p-6 bg-blue-50 rounded-xl">
                  <FileText className="h-8 w-8 text-blue-600 mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-2">CMMI L3 Governance</h3>
                  <p className="text-sm text-gray-600">PMO standards, process audits, and quality assurance frameworks</p>
                </div>
                <div className="p-6 bg-blue-50 rounded-xl">
                  <Users className="h-8 w-8 text-blue-600 mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-2">Strategic Leadership</h3>
                  <p className="text-sm text-gray-600">Cross-functional coordination, escalation management, and stakeholder alignment</p>
                </div>
                <div className="p-6 bg-blue-50 rounded-xl">
                  <GitBranch className="h-8 w-8 text-blue-600 mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-2">Agile Excellence</h3>
                  <p className="text-sm text-gray-600">Scrum & Kanban mastery, sprint velocity optimization, continuous delivery</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Professional Brand Showcase */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-blue-50 border-blue-200 text-blue-700 px-4 py-2 mb-4">
              Professional Overview
            </Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Comprehensive Professional Profile
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A complete snapshot of my expertise, achievements, and professional capabilities
            </p>
          </div>

          <Card className="bg-white border-none shadow-2xl overflow-hidden">
            <CardContent className="p-0">
              <img
                src="/images/banner.png"
                alt="Adarsh Sharma Professional Brand"
                className="w-full h-auto"
              />
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Core Expertise */}
      <section id="expertise" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-blue-50 border-blue-200 text-blue-700 px-4 py-2 mb-4">
              Core Competencies
            </Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Agile Delivery & PMO Expertise
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive capabilities in agile methodologies, CMMI L3 governance, and strategic project leadership
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {expertise.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={`expertise-${item.title}`} className="border-none shadow-md hover:shadow-xl transition-all duration-300 group cursor-pointer">
                  <CardContent className="p-8">
                    <div className="h-12 w-12 bg-blue-50 rounded-xl flex items-center justify-center mb-6 group-hover:bg-blue-100 transition-colors">
                      <Icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Project Case Studies */}
      <section id="projects" className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-white border-gray-200 text-gray-700 px-4 py-2 mb-4">
              Portfolio
            </Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Project Case Studies
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real-world projects showcasing business analysis, stakeholder management, and delivery excellence
            </p>
          </div>

          <div className="space-y-8">
            {projects.map((project, index) => (
              <Card key={`project-${project.title}`} className="bg-white border-none shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-10">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <Badge className="bg-blue-50 text-blue-700 border-blue-200 mb-3">
                        {project.industry}
                      </Badge>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{project.title}</h3>
                      <p className="text-blue-600 font-semibold">{project.role}</p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-2 uppercase tracking-wide">Problem Statement</h4>
                      <p className="text-gray-700">{project.problem}</p>
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-2 uppercase tracking-wide">Approach</h4>
                      <p className="text-gray-700">{project.approach}</p>
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">Key Deliverables</h4>
                      <div className="grid md:grid-cols-2 gap-3">
                        {project.deliverables.map((deliverable, i) => (
                          <div key={`deliverable-${project.title}-${i}`} className="flex items-start gap-2">
                            <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700 text-sm">{deliverable}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3 pt-4">
                      {project.tools.map((tool, i) => (
                        <Badge key={`tool-${project.title}-${tool}`} variant="outline" className="border-gray-300 text-gray-700">
                          {tool}
                        </Badge>
                      ))}
                    </div>

                    <Separator />

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <p className="text-xs font-semibold text-blue-600 mb-1 uppercase tracking-wide">Business Impact</p>
                        <p className="text-gray-900 font-semibold">{project.impact}</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg">
                        <p className="text-xs font-semibold text-green-600 mb-1 uppercase tracking-wide">Outcome</p>
                        <p className="text-gray-900 font-semibold">{project.outcome}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Professional Journey */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-blue-50 border-blue-200 text-blue-700 px-4 py-2 mb-4">
              Career Timeline
            </Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Professional Journey
            </h2>
          </div>

          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-blue-200" />
            
            <div className="space-y-8">
              {journey.map((milestone, index) => (
                <div key={`milestone-${milestone.year}-${milestone.title}`} className="relative flex gap-8">
                  <div className="flex-shrink-0 w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm z-10">
                    {milestone.year}
                  </div>
                  <Card className="flex-1 border-none shadow-md">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{milestone.title}</h3>
                      <p className="text-blue-600 font-semibold mb-3">{milestone.company}</p>
                      <p className="text-gray-700">{milestone.description}</p>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Tools & Knowledge */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-white border-gray-200 text-gray-700 px-4 py-2 mb-4">
              Technical Proficiency
            </Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Tools & Working Knowledge
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tools.map((toolGroup, index) => (
              <Card key={`tools-${toolGroup.category}`} className="bg-white border-none shadow-md">
                <CardContent className="p-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{toolGroup.category}</h3>
                  <div className="flex flex-wrap gap-2">
                    {toolGroup.items.map((tool, i) => (
                      <Badge key={`${toolGroup.category}-${tool}`} className="bg-blue-50 text-blue-700 border-blue-200">
                        {tool}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Resume Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Card className="bg-gradient-to-br from-blue-600 to-indigo-700 border-none shadow-2xl">
            <CardContent className="p-16">
              <h2 className="text-4xl font-bold text-white mb-6">
                Ready to Work Together?
              </h2>
              <p className="text-xl text-blue-100 mb-10">
                Download my complete resume to explore my full professional background, certifications, and detailed project portfolio.
              </p>
              <Button
                size="lg"
                onClick={() => window.open('https://customer-assets.emergentagent.com/job_pmo-excellence-hub/artifacts/zgt4rgwl_Adarsh_Sharma_Resume_Final.pdf', '_blank')}
                className="bg-white text-blue-600 hover:bg-gray-100 px-10 py-7 text-lg font-semibold"
              >
                <Download className="mr-3 h-6 w-6" />
                Download Complete Resume
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-white border-gray-200 text-gray-700 px-4 py-2 mb-4">
              Get in Touch
            </Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Let&apos;s Build Something Great
            </h2>
            <p className="text-xl text-gray-600">
              Open to new opportunities, collaborations, and consulting engagements
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="bg-white border-none shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="h-16 w-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                <a href="mailto:adarshsharma883@gmail.com" className="text-blue-600 hover:text-blue-700 text-sm">
                  adarshsharma883@gmail.com
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white border-none shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="h-16 w-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Phone</h3>
                <a href="tel:+918452834884" className="text-blue-600 hover:text-blue-700 text-sm">
                  +91 84528 34884
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white border-none shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="h-16 w-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Linkedin className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">LinkedIn</h3>
                <a href="https://linkedin.com/in/adarshsharma2026" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 text-sm">
                  Connect on LinkedIn
                </a>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center gap-2 text-gray-600">
              <MapPin className="h-5 w-5 text-blue-600" />
              <span>Pune, Maharashtra, India</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="/images/logo.png"
                alt="Adarsh Sharma"
                className="h-10 w-10 object-contain"
              />
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-gray-900">ADARSH</span>
                  <span className="text-lg font-bold text-blue-600">SHARMA</span>
                </div>
                <p className="text-sm text-gray-600">Agile Delivery Leader & PMO Excellence</p>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <a href="https://linkedin.com/in/adarshsharma2026" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-blue-600 transition-colors">
                <Linkedin className="h-5 w-5" />
              </a>
              <a href="mailto:adarshsharma883@gmail.com" className="text-gray-600 hover:text-blue-600 transition-colors">
                <Mail className="h-5 w-5" />
              </a>
            </div>
          </div>

          <Separator className="my-8" />

          <div className="text-center text-sm text-gray-600">
            <p>© 2026 Adarsh Sharma. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PremiumHome;